import React from 'react';
import classnames from 'classnames';
import DeviationGrid from '../DeviationGrid';
import { PapiDeviation } from '@wix/da-papi-types';
import ContainerSize from '../../../ContainerSize';
import { compose } from 'redux';
import { getDeviationEntityId } from '../../../redux/normalizr/schemas/deviation';
import SelectableDeviationWrapper from '../../_partials/SelectableDeviationWrapper';
import DeviationDumbView from '../../../DeviationViews/Dumbo';
import withFilter, { WithFilterProps } from '../../_hocs/withFilter';
import PollForThumbReady from '../../../DeviationViews/Helper/PollForThumbReady';
import {
  getDeviationResourceType,
  getDeviationType,
  getPrimaryTier,
  isDeviationAuthor,
  isFileLargerThanSizeOrEqual,
  isFileSizeBetween,
} from '../../../DeviationViews/_helpers';
import { getIsGif } from '../../../redux/deviations/selectors';
import MediaService from '../../../utils/DeviationMediaService';
import { useCurrentUser } from '../../../utils/hooks/useCurrentUser';
import { normalizePremiumFolderData } from '../../../utils/normalizePremiumFolderData';
import { DeviationTypes } from '../../../types/deviation';

import deviationSelectorStyles from './DeviationSelector.scss';

export type Props = { deviations: PapiDeviation[] };

const enhance = compose(withFilter);

const DeviationSelector: React.FC<Props & WithFilterProps> = ({
  deviations,
  allowedDeviationTypes,
  allowedDeviationResourceTypes,
  allowGifs,
  allowPremium,
  allowInTierContent,
  allowMature,
  minDeviationWidth,
  minDeviationHeight,
  minFileSize,
  maxFileSize,
  allowDefaultImage,
  ignoreDeviationIds = [],
}) => {
  const currentUser = useCurrentUser();
  const deviationRenderer = (deviation: PapiDeviation) => {
    const isInTier = !!getPrimaryTier(deviation);
    const isOwner = isDeviationAuthor(deviation, currentUser);

    let isSelectionAllowed = !ignoreDeviationIds.includes(
      deviation.deviationId
    );
    const isGif = getIsGif(deviation);
    const { isPremium } = normalizePremiumFolderData(
      deviation?.premiumFolderData
    );
    const isDefaultImage = deviation?.isDefaultImage;

    const deviationType = getDeviationType(deviation);

    if (
      (!allowPremium && isPremium) ||
      (!allowMature && deviation.isMature) ||
      (!allowInTierContent && isInTier) ||
      (isInTier && !isOwner) ||
      (!allowGifs && isGif) ||
      (!allowDefaultImage && isDefaultImage) ||
      (allowedDeviationTypes &&
        !allowedDeviationTypes.includes(deviationType)) ||
      (allowedDeviationResourceTypes &&
        !allowedDeviationResourceTypes.includes(
          getDeviationResourceType(deviation)
        ))
    ) {
      isSelectionAllowed = false;
    }

    if (minDeviationWidth || minDeviationHeight) {
      isSelectionAllowed =
        isSelectionAllowed &&
        isFileLargerThanSizeOrEqual(
          MediaService.getLargestFullview(deviation),
          minDeviationWidth,
          minDeviationHeight
        );
    }

    if (minFileSize !== undefined || maxFileSize !== undefined) {
      isSelectionAllowed =
        isSelectionAllowed &&
        isFileSizeBetween(
          MediaService.getLargestFullview(deviation),
          minFileSize,
          maxFileSize
        );
    }

    const isStatus = deviationType === DeviationTypes.STATUS;

    return (
      <ContainerSize
        className={deviationSelectorStyles['thumb-measured-container']}
      >
        {({ width, height }) => (
          <SelectableDeviationWrapper
            key={getDeviationEntityId(deviation)}
            deviation={deviation}
            isDisabled={!isSelectionAllowed}
          >
            <PollForThumbReady deviation={deviation}>
              <DeviationDumbView
                className={classnames(
                  isStatus && deviationSelectorStyles['status-thumb']
                )}
                deviation={deviation}
                width={width}
                height={height}
              />
            </PollForThumbReady>
          </SelectableDeviationWrapper>
        )}
      </ContainerSize>
    );
  };
  return (
    <DeviationGrid
      deviations={deviations}
      deviationRenderer={deviationRenderer}
    />
  );
};
DeviationSelector.displayName = 'DeviationSelector';

export default enhance(DeviationSelector) as React.ComponentType<Props>;
